import type { RenderBox, RenderGroupBox, RenderObjectBox } from '@lite-workspace/shared/render-model';
import { rotatedAABB } from './overlay-transform';
import { scaleMaskRect } from '@lite-workspace/photobook-model';

/**
 * Compound-clip re-bake (FKS-9594, restored in FKS-12999).
 *
 * A PSD clipping mask whose base is text renders as a texture OBJECT masked by the letters. The
 * parser bakes that silhouette once at import, so any later change to the text — typed, restyled,
 * moved, rewritten by a command, undone — would leave the texture cut to the OLD word.
 * `compoundClipBakeRecipe` names the boxes the silhouette came from; the editor re-renders exactly
 * those boxes through the same engine that paints the canvas and hands the fresh silhouette back
 * as the clipper's mask.
 *
 * This file is the pure half: which clippers there are, the scene that draws each one's
 * silhouette, and a key that changes whenever that scene would. Rasterizing is the caller's job.
 */

/** A clipper box and the silhouette PNG it should carry from now on. */
export interface CompoundClipMaskUpdate {
  readonly boxId: string;
  readonly maskDataUrl: string;
  readonly maskRect?: RenderBox['rect'];
}

export interface CompoundClipRebakePlan {
  /** Actual produced mask frame in the clipper parent's coordinates; omitted for the legacy source-sized contract. */
  readonly maskRect?: RenderBox['rect'];
  /** OBJECT box whose `maskDataUrl` this plan replaces. */
  readonly clipperBoxId: string;
  /**
   * The source boxes, each wrapped in a group that carries it into the clipper's own frame — the
   * scene builders add a group's offset to everything inside (rects, glyphs, text paths), exactly
   * as they do on the canvas, so the silhouette lands where the texture is.
   */
  readonly boxes: readonly RenderBox[];
  readonly width: number;
  readonly height: number;
  /** Scene scale for the raster — keeps an oversized silhouette within `MAX_MASK_PIXELS`. */
  readonly scale: number;
  /** Changes whenever the silhouette scene would: compare it to decide whether the mask is stale. */
  readonly key: string;
}

/**
 * A full-spread clipper at 300 DPI would ask for a ~23 MP RGBA buffer per re-bake. The mask is
 * sampled bilinearly at render time, so capping its raster costs nothing visible.
 */
export const MAX_MASK_PIXELS = 8_000_000;

interface PlacedBox {
  readonly box: RenderBox;
  /** Where the box's parent frame sits on the spread — group children carry parent-local rects. */
  readonly parentX: number;
  readonly parentY: number;
}

export function planCompoundClipRebakes(boxes: readonly RenderBox[]): CompoundClipRebakePlan[] {
  const placed = placeBoxes(boxes);
  const plans: CompoundClipRebakePlan[] = [];

  for (const clipper of placed.values()) {
    if (clipper.box.type !== 'object') continue;
    const recipe = (clipper.box as RenderObjectBox).compoundClipBakeRecipe;
    if (!recipe || recipe.preserveStaticMask) continue;

    const { rect } = clipper.box;
    const explicitFrame = clipper.box.layerStyleRendering === 'live' || !!clipper.box.maskRect?.transform;
    const angle = explicitFrame ? 0 : (rect.rotation ?? 0);
    // A sheared texture would need the silhouette counter-sheared into its frame; keep its imported mask.
    if ((clipper.box as { transform?: unknown }).transform) continue;
    if (rect.width <= 0 || rect.height <= 0) continue;

    const sources = recipe.leafBoxIds.map(id => placed.get(id)).filter((s): s is PlacedBox => !!s);
    // Every source gone (a leaf deleted, ids remapped) — re-baking would blank the clipper out,
    // so leave the imported silhouette standing instead.
    if (sources.length === 0) continue;
    // Rotated as a pair the local geometry is unchanged. Rotated apart, an imported mask stays as imported; a bond
    // without a mask (⌥⌘G) is baked with the real angle difference — the clip works at any angle, as in Photoshop.
    const apart = sources.some(s => (s.box.rect.rotation ?? 0) !== angle);
    if (!explicitFrame && apart && (clipper.box as RenderObjectBox).maskDataUrl) continue;

    let frame = rect;
    if (explicitFrame) {
      const initial: NonNullable<RenderBox['maskRect']> = clipper.box.maskRect ?? {
        x: recipe.groupBounds.left,
        y: recipe.groupBounds.top,
        width: recipe.groupBounds.right - recipe.groupBounds.left,
        height: recipe.groupBounds.bottom - recipe.groupBounds.top,
      };
      const maskBounds = initial.transform ? scaleMaskRect(initial, 1, 1) : rotatedAABB(initial, initial.rotation ?? 0);
      const bounds = [
        maskBounds,
        ...sources.map(source =>
          rotatedAABB(
            {
              ...source.box.rect,
              x: source.box.rect.x + source.parentX - clipper.parentX,
              y: source.box.rect.y + source.parentY - clipper.parentY,
            },
            source.box.rect.rotation ?? 0,
          ),
        ),
      ];
      const x = Math.min(...bounds.map(b => b.x)),
        y = Math.min(...bounds.map(b => b.y));
      frame = {
        x,
        y,
        width: Math.max(...bounds.map(b => b.x + b.width)) - x,
        height: Math.max(...bounds.map(b => b.y + b.height)) - y,
      };
    }

    const originX = clipper.parentX + frame.x;
    const originY = clipper.parentY + frame.y;
    const sceneBoxes = sources.map(source => toClipperFrame(clipper.box.id, source, originX, originY, frame, angle));
    const scale = maskScaleFor(frame.width, frame.height);
    const width = Math.max(1, Math.round(frame.width * scale));
    const height = Math.max(1, Math.round(frame.height * scale));

    plans.push({
      clipperBoxId: clipper.box.id,
      boxes: sceneBoxes,
      width,
      height,
      scale,
      ...(explicitFrame && { maskRect: { x: frame.x, y: frame.y, width: width / scale, height: height / scale } }),
      key: JSON.stringify([width, height, angle, sceneBoxes]),
    });
  }

  return plans;
}

/**
 * Turns a rendered RGBA frame into the mask the renderer expects: colour dropped, alpha kept —
 * the same shape the parser bakes at import (`alphaToLayer` over black).
 */
export function maskDataUrlFromRgba(rgba: Uint8ClampedArray, canvas: HTMLCanvasElement): string | undefined {
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  const image = ctx.createImageData(canvas.width, canvas.height);
  for (let i = 0; i < image.data.length; i += 4) {
    image.data[i] = 0;
    image.data[i + 1] = 0;
    image.data[i + 2] = 0;
    image.data[i + 3] = rgba[i + 3] ?? 0;
  }

  ctx.putImageData(image, 0, 0);

  return canvas.toDataURL('image/png');
}

function maskScaleFor(width: number, height: number): number {
  const pixels = width * height;
  return pixels <= MAX_MASK_PIXELS ? 1 : Math.sqrt(MAX_MASK_PIXELS / pixels);
}

/**
 * Places a source into the clipper's own upright frame. The source keeps its rect (glyphs, a text path stay anchored
 * to it) and the wrapping group carries it: for an upright pair that is the parent offset, for a turned clipper the
 * source centre is measured from the clipper centre and turned back, so the mask is baked as the upright texture
 * sees it; the source keeps only its turn relative to the clipper.
 */
function toClipperFrame(
  clipperId: string,
  source: PlacedBox,
  originX: number,
  originY: number,
  frame: RenderBox['rect'],
  angle: number,
): RenderBox {
  const r = source.box.rect;
  const dx = source.parentX + r.x + r.width / 2 - (originX + frame.width / 2);
  const dy = source.parentY + r.y + r.height / 2 - (originY + frame.height / 2);
  const a = (-angle * Math.PI) / 180;
  const localX = frame.width / 2 + (dx * Math.cos(a) - dy * Math.sin(a)) - r.width / 2;
  const localY = frame.height / 2 + (dx * Math.sin(a) + dy * Math.cos(a)) - r.height / 2;
  let child = source.box;
  // A hidden source still shapes nothing on the canvas; draw it anyway — the silhouette was baked with it.
  if (child.visible === false) child = { ...child, visible: true } as RenderBox;
  // Inside the upright clipper frame the source turns by its own angle minus the clipper's.
  const relative = (r.rotation ?? 0) - angle;
  if ((child.rect.rotation ?? 0) !== relative) child = { ...child, rect: { ...child.rect, rotation: relative } } as RenderBox;

  return {
    type: 'group',
    id: `${clipperId}:mask:${source.box.id}`,
    rect: { x: localX - r.x, y: localY - r.y, width: frame.width, height: frame.height },
    children: [child],
  } as RenderGroupBox;
}

/** Clippers (OBJECT boxes with a recipe) whose base is one of `ids`, in tree order. */
export function clipCompanionRenderIds(boxes: readonly RenderBox[], ids: readonly string[]): string[] {
  const wanted = new Set(ids);
  const out: string[] = [];
  const walk = (list: readonly RenderBox[]): void => {
    for (const box of list) {
      const recipe = box.type === 'object' ? (box as RenderObjectBox).compoundClipBakeRecipe : undefined;
      if (recipe?.leafBoxIds.some(id => wanted.has(id))) out.push(box.id);
      if (box.type === 'group') walk((box as RenderGroupBox).children);
    }
  };

  walk(boxes);

  return out;
}

function placeBoxes(boxes: readonly RenderBox[]): Map<string, PlacedBox> {
  const placed = new Map<string, PlacedBox>();
  const walk = (list: readonly RenderBox[], parentX: number, parentY: number): void => {
    for (const box of list) {
      placed.set(box.id, { box, parentX, parentY });
      if (box.type === 'group') walk((box as RenderGroupBox).children, parentX + box.rect.x, parentY + box.rect.y);
    }
  };

  walk(boxes, 0, 0);

  return placed;
}
